import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, effect, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ModalService } from '@services/modal.service';
import { NotificationsService } from '@services/notifications.service';
import { ResponsiveService } from '@services/responsive.service';
import { TimezoneMode, TimezoneOption, TimezoneService } from '@services/timezone.service';
import { UserService } from '@services/user.service';
import { DropdownComponent } from '@shared/elements/dropdown/dropdown.component';
import { ImageComponent } from '@shared/elements/image/image.component';
import { SvgIconComponent } from '@shared/elements/svg-icon/svg-icon.component';
import { MembershipStatusType, USER_MEMBERSHIP_STATUS } from '@shared/enums/membership';
import { isAdminUser } from '@shared/general';
import { getCurrentMembershipStatus, getMemberStatus } from '@shared/helpers/membership.helper';
import { checkForPreviewMode } from '@shared/helpers/system.helper';
import { MembershipRecord } from '@shared/models/membership.model';
import { daysFromToday } from '@shared/time';
import dayjs from 'dayjs';
import { distinctUntilChanged, finalize, Observable } from 'rxjs';
import { Admin } from '../../../shared/models/admin.models';
import { ResetPasswordComponent } from '../../admin/users/reset-password/reset-password.component';
import { LocalStorageService } from '../../services/local-storage.service';

interface MenuOption {
  icon: string;
  option: string;
  action: string;
}

interface NavigationRoute {
  name: string;
  route: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [NgClass, RouterLink, SvgIconComponent, ImageComponent, AsyncPipe, DropdownComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  readonly responsive = inject(ResponsiveService);
  readonly notification = inject(NotificationsService);
  readonly router = inject(Router);

  private readonly storage = inject(LocalStorageService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  private readonly timezoneService = inject(TimezoneService);

  isMenuCollapsed = false;
  showDropdown = false;
  moduleName = 'UserModule';
  logoId?: string | number;
  isAdmin = false;
  accountName = '';
  role: 'User' | 'Admin' | 'Non-Authorized' = 'Admin';
  title = '';
  status: USER_MEMBERSHIP_STATUS = 0;
  currentMembership?: MembershipRecord;
  futureMembership?: MembershipRecord;

  options: MenuOption[] = [
    { option: 'Profile', action: 'profile', icon: 'action_view' },
    { option: 'Go To User View', action: 'user_screens', icon: 'action_view' },
    { option: 'Settings', action: 'settings', icon: 'menu_settings' },
    { option: 'Switch Theme', action: 'theme', icon: 'sun' },
    { option: 'Switch Timezone', action: 'timezone', icon: 'clock' },
    { option: 'Change password', action: 'action_password', icon: 'action_key' },
    { option: 'Notifications', action: 'action_preferences', icon: 'email' },
    { option: 'Help', action: 'help', icon: 'help' },
    { option: 'Sign Out', action: 'sign_out', icon: 'out' },
  ];

  displayedOptions: MenuOption[] = [];

  readonly timezoneOptions = this.timezoneService.timezoneOptions;
  readonly timezoneSelectionEnabled = this.timezoneService.selectionEnabled;
  timezoneKeys = { value: 'value', label: 'label' };
  timezoneControl = new FormControl<TimezoneMode>(this.timezoneService.mode());

  readonly currentTimezoneLabel = computed(() => {
    const options = this.timezoneOptions();
    const current = options.find((o) => o.value === this.timezoneService.effectiveMode()) ?? options[0];
    return current ? this.resolveTimezoneName(current) : '';
  });

  routes: NavigationRoute[] = [
    { name: 'About Us', route: 'general/about-us' },
    { name: 'Events', route: 'general/events' },
    { name: 'News', route: 'general/news' },
    { name: 'Gallery', route: 'general/gallery' },
    { name: 'Donation', route: 'general/donation' },
    { name: 'Membership', route: 'general/memberships' },
    { name: 'Contact Us', route: 'general/contact-us' },
  ];

  private modalIndex = 0;
  membershipStatus: MembershipStatusType = 'NONE';
  isPreviewMode = checkForPreviewMode();
  showMenu = false;

  isMobile$?: Observable<boolean>;

  @ViewChild('menuWrapper') menuWrapper?: ElementRef;
  @ViewChild('mobileMenuWrapper') mobileMenuWrapper?: ElementRef;

  @HostListener('document:click', ['$event'])
  closeMenuOnClickOutside(event: MouseEvent) {
    if (!this.menuWrapper?.nativeElement.contains(event.target)) {
      this.showDropdown = false;
    }

    if (!this.mobileMenuWrapper?.nativeElement.contains(event.target)) {
      this.showMenu = false;
    }
  }

  constructor() {
    this.isMobile$ = this.responsive.isMobile$();

    this.timezoneControl.valueChanges.subscribe((mode) => {
      if (mode) {
        this.timezoneService.setMode(mode);
      }
    });

    effect(() => {
      this.timezoneOptions();
      setTimeout(() => this.timezoneControl.setValue(this.timezoneService.mode()));
    });

    effect(() => {
      const currentModule = this.storage.currentModule();
      this.moduleName = currentModule === 'Home' ? '' : currentModule;

      const user = this.storage.userInfo();
      this.isAdmin = !!user && isAdminUser(user);
      this.isPreviewMode = this.storage.previewUserScreens() && this.isAdmin;

      if (this.isAdmin) {
        const excludedActions = ['profile', 'help', 'action_preferences', 'timezone'];
        this.displayedOptions = this.options.filter((i) => !excludedActions.includes(i.action));
        this.changeOptionName();
      }

      this.role = user ? (this.isAdmin ? 'Admin' : 'User') : 'Non-Authorized';
      this.accountName = `${user?.firstName} ${user?.lastName}`;

      if (user && !this.isAdmin) {
        const excludedActions = ['user_screens', 'admin_screens', 'settings', 'theme'];
        if (this.timezoneService.timezoneOptions().length < 2) {
          excludedActions.push('timezone');
        }
        this.displayedOptions = this.options.filter((i) => !excludedActions.includes(i.action));
        this.updateTimezoneMenuLabel();
        this.checkMembership();
      }

      if (!user) {
        this.moduleName = '';
        this.accountName = ` `;
        this.currentMembership = this.futureMembership = undefined;
      }
    });

    const collapsedMenu = localStorage.getItem('collapsedMenu');

    if (collapsedMenu) {
      this.collapseMenu();
    }

    this.userService.getSiteInfo().subscribe((res) => {
      this.logoId = res.data.siteLogo?.id;
      this.title = res.data?.siteTitle;
      this.storage.logoId.set(this.logoId);
    });
  }

  checkMembership() {
    this.userService
      .getUserInfo()
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev.data) !== JSON.stringify(curr.data)))
      .subscribe((res) => {
        this.storage.membershipInfo.set(res.data);
        this.status = getMemberStatus(res.data);

        if ((res.data?.futureMemberships ?? []).length) {
          this.futureMembership = res.data?.futureMemberships[0];
        } else {
          this.futureMembership = undefined;
        }

        if (res.data?.currentMembership) {
          this.currentMembership = res.data.currentMembership;
          this.showMembershipWarningsIfNecessary();
        }
      });
  }

  showMembershipWarningsIfNecessary(): void {
    const membership = this.currentMembership?.membership;

    if (membership) {
      if (membership?.memberType?.name === 'LIFE') {
        this.membershipStatus = 'ACTIVE';
        return;
      }

      const countdownToExpiry = daysFromToday(this.currentMembership?.expiryDate);

      this.membershipStatus = getCurrentMembershipStatus(membership, countdownToExpiry);
      const currentExpiry = dayjs.utc(this?.futureMembership?.startDate).startOf('day');
      const futureStart = dayjs.utc(this.currentMembership?.expiryDate).startOf('day');

      if (!(
        this.status === 4 &&
        (this.currentMembership?.membership?.id === this.futureMembership?.membership?.id ||
          currentExpiry.isSame(futureStart) ||
          futureStart.isBefore(currentExpiry))
      )) {
        const gracePeriodDays = membership?.gracePeriod ?? 0;
        this.showNotificationBasedOnMembershipStatus(this.membershipStatus, countdownToExpiry, gracePeriodDays);
      }
    }
  }

  showNotificationBasedOnMembershipStatus(status: MembershipStatusType, daysLeft: number, gracePeriodDays: number) {
    if (status === 'ATTENTION') {
      const message =
        daysLeft < 2
          ? `Your membership ends ${daysLeft < 1 ? 'today' : 'tomorrow'}`
          : `Your membership expires in ${daysLeft} days. Please renew before losing all the benefits`;
      this.showWarning('Membership Expiring', message);
    } else if (status === 'GRACE_ZONE') {
      const graceDaysLeft = gracePeriodDays + daysLeft;
      this.showWarning(
        'Grace Period',
        `Grace Period ends ${graceDaysLeft ? 'in ' + graceDaysLeft + ' day(s)' : 'today'}. Please renew before you lose all the benefits`,
      );
    }
  }

  showWarning(title: string, message: string, time = 30000) {
    this.notification.warning(title, message, time);
  }

  collapseMenu(): void {
    this.storage.toggleMenu();
    this.isMenuCollapsed = this.storage.isMenuCollapsed();
    localStorage.setItem('collapsedMenu', this.storage.isMenuCollapsed() ? 'true' : '');
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  performAction(action: string): void {
    switch (action) {
      case 'theme':
        this.storage.toggleTheme();
        break;
      case 'timezone':
        this.cycleTimezone();
        break;
      case 'sign_out':
        this.signOut();
        break;
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'action_password':
        this.changePassword();
        break;
      case 'action_preferences':
        this.router.navigate(['/profile'], { queryParams: { tab: 6 } });
        break;
      case 'help':
        this.router.navigate(['/general/contact-us']);
        break;
      case 'user_screens':
        this.changeScreensMode();
        break;
    }
  }

  signOut(): void {
    const sessionId = Number(localStorage.getItem('SessionId'));
    const data = { id: sessionId, logDesc: true };
    this.userService
      .logout(data)
      .pipe(
        finalize(() => {
          this.storage.changeLoggedIn(false);
          this.userService.cleanAllCache();
          this.router.navigate(['/login']);
        }),
      )
      .subscribe();
  }

  cycleTimezone(): void {
    const cycle = this.getTimezoneCycle();
    if (!cycle) return;

    this.timezoneService.setMode(cycle.next.value);
  }

  private resolveTimezoneName(option: TimezoneOption): string {
    return option.value === 'local' ? this.timezoneService.localTimeZone : option.label;
  }

  private getTimezoneCycle(): { current: TimezoneOption; next: TimezoneOption } | null {
    const options = this.timezoneService.timezoneOptions();
    if (options.length < 2) return null;

    const currentIndex = options.findIndex((o) => o.value === this.timezoneService.mode());
    const current = options[currentIndex] ?? options[0];
    const next = options[(currentIndex + 1) % options.length];
    return { current, next };
  }

  updateTimezoneMenuLabel(): void {
    const item = this.displayedOptions.find((i) => i.action === 'timezone');
    if (!item) return;

    const cycle = this.getTimezoneCycle();
    if (!cycle) return;

    item.option = `Switch Timezone: ${this.resolveTimezoneName(cycle.current)} → ${this.resolveTimezoneName(cycle.next)}`;
  }

  changeScreensMode(): void {
    const preview = checkForPreviewMode();
    if (!preview) {
      this.storage.changeCurrentModule('');
      localStorage.setItem('MODE', 'USER_SCREENS');
      this.storage.previewUserScreens.set(true);
      this.router.navigate(['/home']);
    } else {
      localStorage.removeItem('MODE');
      this.storage.previewUserScreens.set(false);
      this.storage.toggleTheme('dark');
      this.router.navigate(['/admin/dashboard']);
    }

    this.changeOptionName();
  }

  changeOptionName(preview: boolean = this.isPreviewMode) {
    if (this.isAdmin) {
      this.displayedOptions.find((i) => i.action === 'user_screens')!.option =
        `Go To ${preview ? 'Admin' : 'User'} View`;
    }
  }

  changePassword() {
    const userInfo = this.storage.userInfo();
    if (this.role === 'User') {
      this.router.navigate(['/profile'], { queryParams: { tab: 2 } });
    } else if (this.isAdmin && userInfo) {
      this.modalIndex = this.modalService.modalCreate<Admin>(ResetPasswordComponent, {
        action: 'reset_password',
        data: userInfo as any,
      });
    }
  }
}
